import { TradingApi } from "@trader/shared/Api"
import { Effect, ManagedRuntime, Predicate } from "effect"
import { FetchHttpClient } from "effect/http"
import { HttpApiClient } from "effect/http-api"

// The only place the frontend talks HTTP. The client is derived from the
// shared TradingApi, so endpoint and schema changes are compile errors here.

type Client = HttpApiClient.ForApi<typeof TradingApi>

const runtime = ManagedRuntime.make(FetchHttpClient.layer)
const client = runtime.runPromise(HttpApiClient.make(TradingApi, { baseUrl: window.location.origin }))

/** A failed API call. `tag` is the server's error type, e.g. "ProviderNotConnected". */
export class ApiError extends Error {
  readonly tag: string
  readonly detail: unknown

  constructor(tag: string, detail: unknown) {
    super(tag)
    this.tag = tag
    this.detail = detail
  }
}

const unauthorizedListeners = new Set<() => void>()

/** Called whenever any request comes back 401, so the app can show the login page. */
export const onUnauthorized = (listener: () => void): (() => void) => {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

/** Runs one API call and returns a promise, rejecting with an ApiError. */
export const call = async <A, E>(request: (client: Client) => Effect.Effect<A, E>): Promise<A> => {
  const result = await runtime.runPromise(Effect.result(request(await client)))
  if (result._tag === "Success") return result.success
  const tag = Predicate.hasProperty(result.failure, "_tag") ? String(result.failure._tag) : "UnknownError"
  if (tag === "Unauthorized") for (const listener of unauthorizedListeners) listener()
  throw new ApiError(tag, result.failure)
}

export const errorTag = (error: unknown): string => (error instanceof ApiError ? error.tag : "UnknownError")

/** A field of the server's error, as text, e.g. the `provider` of ProviderNotConnected. */
export const errorField = (error: unknown, field: string): string | undefined =>
  error instanceof ApiError && Predicate.hasProperty(error.detail, field) ? String(error.detail[field]) : undefined
